import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { OptionType } from '../bx-select.types';
import type { SourceConfigValue, SourceConfigProps, SourceRelationOption, SourceRelationValue } from './types';
import type { HelperLocation, BomType, SampleItem, PlacementItem, AttributeDetail, CustomColumn, SubLocation, SubLocationDef, RootState } from './types';
import { toasterMsg } from '../../Toaster/Toaster';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { cmControlBaseLocations, cmControlSublocations, customModuleLocations, PRODUCT_CORE_LOCATION_ID } from './cm-control-config.constants';
import {
  customSectionPlacementArry,
  productCpAttributeList,
  productCpoutComeList,
  bomSelection,
  prouctBomOutComeList,
  prouctCustmSectionOutComeList,
  prouctCustmSectionAttributeList,
  poProductoutComeList,
  filterCustomAttributesDropdownAndTextType,
  ColumnValues,
} from '../../helper';
import { fetchCustomColumnList, fetchCustomSectionBomTypeAction, getAllMeasurment, getSampleTrackingDates } from 'Pages/RangePlan/CustomSections/store/CustomSections.action';
import { getCustomAttributeDetail, getMainSampleDetails, getMeasurementDetails, getOtherSamplesDetails, getPlacementDetails } from 'Pages/SystemMasters/Store/SystemMasters.action';
import { getEcommSpec } from 'Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import LibraryTitleInput from '../library-title-input';
import { SaveIcon } from '../../../bombiix-icon';
import { NewInput } from '../../PIM/common/NewInput';
import { NewSelectMultiSelect } from '../../PIM/common/NewSelectMultiSelect';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';

/**
 * The master-design select reports `Option | readonly Option[] | null`. Every call site
 * here is a single-select, so narrow to the one option's value.
 * @param option value handed back by NewSelectMultiSelect
 * @returns the selected option's value, or '' when cleared
 */
const selectedValue = (option: OptionType | readonly OptionType[] | null | undefined): string =>
  Array.isArray(option) ? '' : ((option as OptionType | null)?.value ?? '');


const TOAST_DANGER = 'danger';

const buildOption = (label: string, name: string, _id?: string | number): SourceRelationOption => {
  const base: SourceRelationOption = { label, name };
  if (_id !== undefined) {
    base._id = _id;
  }
  return base;
};

const baseLocationOptions: SourceRelationOption[] = (cmControlBaseLocations as unknown as HelperLocation[]).map((loc) => {
  const base: SourceRelationOption = { label: loc.label, name: loc.name };
  if (loc._id !== undefined) base._id = loc._id;
  return base;
});

const customModuleLocationOptions: SourceRelationOption[] = (customModuleLocations as unknown as HelperLocation[]).map((loc) => {
  const base: SourceRelationOption = { label: loc.label, name: loc.name };
  if (loc._id !== undefined) base._id = loc._id;
  return base;
});

const locationOptions: SourceRelationOption[] = [...baseLocationOptions, ...customModuleLocationOptions];

const emptyRelation: SourceRelationValue = {
  location: null,
  locationType: null,
  locationPlacement: null,
  attribute: null,
  output: null,
  subLocation: null,
};

const isDeepEqual = (a: unknown, b: unknown): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * SourceConfig component for configuring source form controls in the system canvas.
 * Allows users to set up data source relationships with location, type, placement, attribute, output, and sub-location selections.
 * Supports complex data mapping from various system entities like BOM, samples, product information, etc.
 * Includes advanced features like colour coding and dynamic option loading based on selections.
 * @param value - Current source configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param disabled - Whether the configuration controls should be disabled
 * @param showLibraryTitle - Whether to show the library title input section
 */
const SourceControl: React.FC<SourceConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const showWidthRow = onWidthChange != null;
  const dispatch = useDispatch();
  const reducer = useSelector((state: RootState) => state);

  const {
    customSectionData: { bomTypes, sampleTrackingDates, customColumnList },
    SystemMastersDetails: { placement_master, subBrandAccess, customAttributeDetail },
  } = reducer;

  const [localValue, setLocalValue] = useState<SourceConfigValue>({
    title: value.title ?? '',
    required: !!value.required,
    isVisibleToSupplier: value.isVisibleToSupplier ?? true,
    relation: value.relation ?? emptyRelation,
    colorSwitch: value.colorSwitch ?? false,
    colourArray: value.colourArray ?? [
      { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
      { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
      { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
    ],
  });

  const [dynamicBomSelection, setDynamicBomSelection] = useState<SourceRelationOption[]>([]);
  const [manualCustomColumns, setManualCustomColumns] = useState<SourceRelationOption[]>([]);
  const [libraryTitle, setLibraryTitle] = useState<string>('');
  const lastEmittedRef = useRef<SourceConfigValue | null>(null);
  const didInitRef = useRef<boolean>(false);

  useEffect(() => {
    const userId = reducer?.UserDetails?.userInfo?.userId;
    (dispatch as unknown as (a: unknown) => unknown)(fetchCustomSectionBomTypeAction({ isAllRecord: true }));
    if (userId) {
      (dispatch as unknown as (a: unknown) => unknown)(getPlacementDetails({ loggedInUserId: userId, isActive: true }));
      (dispatch as unknown as (a: unknown) => unknown)(getMainSampleDetails({ loggedInUserId: userId }));
      (dispatch as unknown as (a: unknown) => unknown)(getOtherSamplesDetails({ loggedInUserId: userId }));
    }
    (dispatch as unknown as (a: unknown) => unknown)(getAllMeasurment());
    (dispatch as unknown as (a: unknown) => unknown)(getSampleTrackingDates());
    (dispatch as unknown as (a: unknown) => unknown)(getCustomAttributeDetail({ isActive: true }));
  }, [dispatch, reducer?.UserDetails?.userInfo?.userId]);

  useEffect(() => {
    const serverTypes: SourceRelationOption[] =
      ((bomTypes || []) as BomType[])
        .filter((t) => (Object.prototype.hasOwnProperty.call(t ?? {}, 'isActive') ? (t as BomType)?.isActive === true : true))
        .map((bt) => buildOption(bt.name, bt._id)) ?? [];
    const staticTypes: SourceRelationOption[] = ((bomSelection || []) as Array<{ label: string; name: string }>).map((selectionItem) =>
      buildOption(selectionItem.label, selectionItem.name),
    );
    const seen = new Set<string>();
    const merged: SourceRelationOption[] = [];
    [...serverTypes, ...staticTypes].forEach((option) => {
      const key = String(option.name);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(option);
      }
    });
    const allIdx = merged.findIndex((o) => o.name === 'all_item');
    if (allIdx > 0) {
      const allItem = merged[allIdx];
      if (allItem) {
        merged.splice(allIdx, 1);
        merged.unshift(allItem);
      }
    }
    setDynamicBomSelection(merged);
  }, [bomTypes]);

  useEffect(() => {
    if (customColumnList) {
      const manualCols =
        ((customColumnList?.columns ?? []) as CustomColumn[])
          .filter((c) => c?.columnType === 'manual' && (c?.type === 'Number' || c?.type === 'Number_percentage'))
          .map((column) => buildOption(column?.title, column?._id)) || [];
      setManualCustomColumns(manualCols);
    }
  }, [customColumnList]);

  useEffect(() => {
    const nextRelation: SourceRelationValue = {
      ...(value.relation ?? {}),
      colorSwitch: value.relation?.colorSwitch ?? value.colorSwitch ?? false,
      colourCoding: value.relation?.colourCoding ??
        value.colourArray ?? [
          { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
        ],
    };
    const next: SourceConfigValue = {
      title: value.title ?? '',
      required: !!value.required,
      isVisibleToSupplier: value.isVisibleToSupplier ?? true,
      relation: nextRelation,
      colorSwitch: value.colorSwitch ?? value.relation?.colorSwitch ?? false,
      colourArray: value.colourArray ?? [
        { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
        { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
        { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
      ],
    };
    if (!isDeepEqual(next, localValue)) {
      setLocalValue(next);
    }
    if (!didInitRef.current) didInitRef.current = true;
  }, [value]);

  useEffect(() => {
    const locId = localValue.relation?.location?._id as number | string | undefined;
    const locName = localValue.relation?.location?.name as string | undefined;
    if (locId === 12 && locName) {
      (dispatch as unknown as (a: unknown) => unknown)(fetchCustomColumnList(locName));
    }
    if (locId === 4) {
      (async () => {
        const ecomValues = (await (dispatch as unknown as (a: unknown) => unknown)(getEcommSpec('eCommSpec'))) as
          | { isActive?: boolean }
          | null
          | undefined;
        if (ecomValues?.isActive) {
          await (dispatch as unknown as (a: unknown) => unknown)(getMeasurementDetails({ isActive: true }));
        }
      })();
    }
  }, [dispatch, localValue.relation?.location]);

  const sampleTypeFromStore: SourceRelationOption[] = useMemo(() => {
    const main = reducer?.SystemMastersDetails?.mainSampleList ?? [];
    const other = reducer?.SystemMastersDetails?.otherSamplesList ?? [];
    return [...main, ...other].map((element) => buildOption(element?.name, element?._id));
  }, [reducer?.SystemMastersDetails?.mainSampleList, reducer?.SystemMastersDetails?.otherSamplesList]);

  const columnValueOptions: ReadonlyArray<OptionType> = useMemo(() => {
    const array = (ColumnValues as unknown as Array<{ _id: string; name: string }>) || [];
    return array.map((columnValue) => ({ label: columnValue.name, value: columnValue._id }));
  }, []);

  /**
   * Gets the label for the selected placement option
   * @returns Label for the current placement selection
   */
  const placementLabel = useCallback(() => {
    const loc = localValue.relation?.location?.name;
    const typeName = localValue.relation?.locationType?.name;
    return loc === 'Bill_of_Materials' && typeName !== 'all_item' ? t('COMMON.PLACEMENT') : t('COMMON.POSITION');
  }, [localValue.relation?.location?.name, localValue.relation?.locationType?.name, t]);

  /**
   * Determines whether to show or hide the placement selection based on location type
   * @returns Boolean indicating if placement should be shown
   */
  const hideShowPlacement = useCallback(() => {
    const locName = localValue.relation?.location?.name;
    const type = localValue.relation?.locationType?.name;
    return (locName === 'Bill_of_Materials' && !!type && type !== 'all_item') || (locName === 'Samples' && !!type);
  }, [localValue.relation?.location?.name, localValue.relation?.locationType?.name]);

  /**
   * Determines whether to show or hide the attribute selection based on location and placement
   * @returns Boolean indicating if attribute selection should be shown
   */
  const hideShowAttribute = useCallback(() => {
    const loc: SourceRelationOption | null | undefined = localValue.relation?.location;
    const locName = loc?.name;
    const locId = loc?._id;
    const placement = localValue.relation?.locationPlacement;
    const type = localValue.relation?.locationType?.name;
    return (
      [
        'Design_Details',
        'Specifications',
        'Testing',
        'Care/User_Document_Information',
        'Costing_Information',
        'Purchase_Order',
        'Product_Core_Information',
        'Product_Information',
        'Supplier_Information',
        'Completion_Indicator',
      ].includes(locName || '') ||
      locId === 12 ||
      (locName === 'Bill_of_Materials' && placement) ||
      (locName === 'Bill_of_Materials' && type === 'all_item') ||
      (locName === 'Samples' && placement?.name)
    );
  }, [localValue.relation?.location, localValue.relation?.locationPlacement, localValue.relation?.locationType]);

  /**
   * Determines whether to show or hide the sub-location selection based on location and attribute
   * @returns Boolean indicating if sub-location selection should be shown
   */
  const hideShowSubLocation = useCallback(() => {
    const loc: SourceRelationOption | null | undefined = localValue.relation?.location;
    const locName = loc?.name;
    const locId = loc?._id;
    const out = localValue.relation?.output?.name;
    const attr = localValue.relation?.attribute?.name;
    const type = localValue.relation?.locationType?.name;
    return (
      ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Costing_Information'].includes(locName || '') ||
      (locId === 12 && localValue.relation?.output) ||
      (locName === 'Bill_of_Materials' && type !== 'all_item' && out === 'valueType') ||
      (locName === 'Samples' && !!attr) ||
      (locName === 'Purchase_Order' && attr === 'valueType') ||
      (locName === 'Completion_Indicator' && !!attr)
    );
  }, [localValue.relation?.location, localValue.relation?.output?.name, localValue.relation?.attribute?.name, localValue.relation?.locationType?.name]);

  /**
   * Determines whether to show or hide the output selection based on location and attribute
   * @returns Boolean indicating if output selection should be shown
   */
  const hideShowOutput = useCallback(() => {
    const loc: SourceRelationOption | null | undefined = localValue.relation?.location;
    const locName = loc?.name;
    const locId = loc?._id;
    const attr = localValue.relation?.attribute?.name;
    if ((locName === 'Bill_of_Materials' || locId === 12) && attr === 'valueType') return false;
    return (
      ((['Design_Details', 'Testing', 'Bill_of_Materials', 'Care/User_Document_Information', 'Custom_Section'].includes(locName || '') || locId === 12) &&
        !!localValue.relation?.attribute?.name) ||
      (locName === 'Purchase_Order' && attr === 'all_item')
    );
  }, [localValue.relation?.location, localValue.relation?.attribute?.name]);

  /**
   * Gets placement options based on the selected location and location type
   * @returns Array of placement options for the current location selection
   */
  const setPlacementForLocation = useCallback((): SourceRelationOption[] => {
    const locName = localValue.relation?.location?.name;
    const selectedBomTypeId = localValue.relation?.locationType?.name;
    if (locName === 'Bill_of_Materials') {
      if (!selectedBomTypeId || selectedBomTypeId === 'all_item') {
        return (placement_master || []).map((value: PlacementItem) => buildOption(value?.name, value?._id));
      }
      const filteredPlacementMasters = (placement_master || []).filter(
        (value: PlacementItem) => Array.isArray(value?.bomType) && (value?.bomType || []).includes(selectedBomTypeId),
      );
      return filteredPlacementMasters.map((value: PlacementItem) => buildOption(value?.name, value?._id));
    }
    return customSectionPlacementArry.map((option) => buildOption(option.label, option.name));
  }, [localValue.relation?.location?.name, localValue.relation?.locationType?.name, placement_master]);

  /**
   * Gets attribute options based on the selected location
   * @returns Array of attribute options for the current location selection
   */
  const setOptionsForAttribute = useCallback((): SourceRelationOption[] => {
    const loc: SourceRelationOption | null | undefined = localValue.relation?.location;
    const locName = loc?.name;
    const locId = loc?._id;
    if (
      [
        'Product_Core_Information',
        'Product_Information',
        'Supplier_Information',
        'Specifications',
        'Samples',
        'Costing_Information',
        'Completion_Indicator',
      ].includes(locName || '')
    ) {
      return [...productCpAttributeList.filter((attribute) => ['valueType'].includes(attribute?.name))].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    if (locName === 'Purchase_Order') {
      return [...productCpAttributeList.filter((attribute) => ['all_item', 'valueType'].includes(attribute?.name))].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    if (locName === 'Bill_of_Materials') {
      return [...productCpAttributeList.filter((attribute) => attribute?.name !== 'valueType')].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    if (['Design_Details'].includes(locName || '')) {
      return [...productCpAttributeList.filter((attribute) => ['first_item', 'last_item'].includes(attribute?.name))].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    if (['Testing', 'Care/User_Document_Information'].includes(locName || '')) {
      return [...productCpAttributeList.filter((attribute) => !['valueType'].includes(attribute?.name))].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    if (locId === 12) {
      return [...productCpAttributeList.filter((attribute) => attribute?.name !== 'all_item')].map((attributeItem) =>
        buildOption(attributeItem.label, attributeItem.name),
      );
    }
    return productCpAttributeList.map((attributeItem) => buildOption(attributeItem.label, attributeItem.name));
  }, [localValue.relation?.location]);

  /**
   * Gets output options for testing-related locations
   * @returns Array of output options for testing scenarios
   */
  const testingOutputList = useCallback((): SourceRelationOption[] => {
    const attr = localValue.relation?.attribute?.name;
    return productCpoutComeList
      .filter((outcomeItem) =>
        ['first_item', 'last_item'].includes(attr || '') ? ['date_added'].includes(outcomeItem?.name) : outcomeItem?.name === 'percentage_of_approval',
      )
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }, [localValue.relation?.attribute?.name]);

  const setOptionsForOutput = useCallback((): SourceRelationOption[] => {
    const loc: SourceRelationOption | null | undefined = localValue.relation?.location;
    const locName = loc?.name;
    const locId = loc?._id;
    const attr = localValue.relation?.attribute?.name;
    if (['Testing', 'Care/User_Document_Information'].includes(locName || '')) {
      return testingOutputList();
    }
    if (locName === 'Design_Details') {
      const extras: SourceRelationOption[] = [{ label: 'Status Date', name: 'status_date' }];
      return [
        ...productCpoutComeList
          .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
          .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name)),
        ...extras,
      ];
    }
    if (locId === 12) {
      return productCpoutComeList
        .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    if (locName === 'Purchase_Order') {
      return poProductoutComeList
        .filter((outcomeItem) => outcomeItem?.name === 'completion_percentage')
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    if (locName === 'Bill_of_Materials') {
      if (localValue.relation?.locationType?.name !== 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
        return productCpoutComeList
          .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
          .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
      }
      return (
        ['first_item', 'last_item'].includes(attr || '')
          ? productCpoutComeList.filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
          : productCpoutComeList.filter((outcomeItem) => outcomeItem?.name === 'percentage_of_approval')
      ).map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    return (prouctCustmSectionOutComeList as unknown as Array<{ label: string; name: string }>).map((outcomeItem) =>
      buildOption(outcomeItem.label, outcomeItem.name),
    );
  }, [localValue.relation?.location, localValue.relation?.locationType?.name, localValue.relation?.attribute?.name, testingOutputList]);

  const setOptionsForBomOutput = useCallback((): SourceRelationOption[] => {
    const locName = localValue.relation?.location?.name;
    const type = localValue.relation?.locationType?.name;
    const attr = localValue.relation?.attribute?.name;
    if (locName === 'Bill_of_Materials' && type !== 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
      return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
        .filter((ele) => ele.name !== 'percentage_of_approval')
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    if (locName === 'Bill_of_Materials' && type === 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
      return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
        .filter((ele) => ele.name === 'date_added')
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    if (locName === 'Bill_of_Materials' && attr === 'all_item') {
      return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
        .filter((ele) => ele.name === 'percentage_of_approval')
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    return (prouctCustmSectionOutComeList as unknown as Array<{ label: string; name: string }>).map((outcomeItem) =>
      buildOption(outcomeItem.label, outcomeItem.name),
    );
  }, [localValue.relation?.location?.name, localValue.relation?.locationType?.name, localValue.relation?.attribute?.name]);

  const setSubLocationArr = useCallback((): SourceRelationOption[] => {
    const locId = localValue.relation?.location?._id;
    const locName = localValue.relation?.location?.name;
    if (locId === 12) return manualCustomColumns;
    let subLocationArr = ((cmControlSublocations || []) as unknown as SubLocationDef[]).find((value) => value?.locationId === (locId as number | string));
    if (!subLocationArr || !subLocationArr.subLocations) {
      const findLocation = (locationOptions as HelperLocation[]).find((l) => l?.name === locName);
      subLocationArr = ((cmControlSublocations || []) as unknown as SubLocationDef[]).find(
        (value) => value?.locationId === (findLocation?._id as number | string),
      );
    }
    const manipulatedDynamicColumns: SubLocation[] = [];
    const mcColumn = filterCustomAttributesDropdownAndTextType((customAttributeDetail || []) as AttributeDetail[]);
    (mcColumn as AttributeDetail[])?.forEach((dyCol) => {
      if ((dyCol as AttributeDetail)?.isActive) manipulatedDynamicColumns.push({ label: dyCol?.keyName, name: dyCol?._id, type: 'string' });
    });
    const sampleTrackingArr: SubLocation[] = ((sampleTrackingDates || []) as SampleItem[]).map((dyCol) => ({
      label: dyCol?.name,
      name: dyCol?._id,
      type: 'date',
    }));
    let subLocations: SubLocation[] = [...(subLocationArr?.subLocations || [])];
    if (locName === 'Product_Information') subLocations = subLocations.concat(manipulatedDynamicColumns);
    if (locName === 'Samples') subLocations = subLocations.concat(sampleTrackingArr);
    if (subLocationArr?.locationId === PRODUCT_CORE_LOCATION_ID && subBrandAccess) {
      subLocations = subLocations.concat([{ label: 'Sub Brand', name: 'subBrand', type: 'string' }]);
    }
    const uniqueSubLocations = subLocations.filter((subLocationItem, index) => subLocations.findIndex((other) => other.name === subLocationItem.name) === index);
    return uniqueSubLocations.map((subLocationItem) => buildOption(subLocationItem.label, subLocationItem.name));
  }, [localValue.relation?.location, customAttributeDetail, sampleTrackingDates, manualCustomColumns, subBrandAccess]);

  const toSelectValue = (option: SourceRelationOption | null | undefined): OptionType | null => (option ? { label: option.label, value: option.name } : null);
  const toOptions = (options: SourceRelationOption[]): OptionType[] => options.map((option) => ({ label: option.label, value: option.name }));

  const setAttrinuteToValue = (getLocation?: string, getLocationPlacment?: string) => {
    return (
      ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Costing_Information', 'Completion_Indicator'].includes(
        getLocation || '',
      ) ||
      (getLocation === 'Samples' && ['earliest', 'mostRecent'].includes(getLocationPlacment || ''))
    );
  };

  const updateRelation = useCallback(
    (key: keyof SourceRelationValue, optionName: string) => {
      setLocalValue((prev) => {
        const currentRelation: SourceRelationValue = prev.relation ?? emptyRelation;
        let nextRelation: SourceRelationValue = { ...currentRelation };
        const pickFrom = (options: SourceRelationOption[]): SourceRelationOption | null => options.find((option) => option.name === optionName) ?? null;

        if (key === 'location') {
          nextRelation = {
            ...emptyRelation,
            location: pickFrom(locationOptions),
          };
          const selected = locationOptions.find((o) => o.name === optionName);
          if (selected?._id === 12) (dispatch as unknown as (a: unknown) => unknown)(fetchCustomColumnList(selected.name));
          if (selected?._id === 4) {
            (async () => {
              const ecomValues = (await (dispatch as unknown as (a: unknown) => unknown)(getEcommSpec('eCommSpec'))) as
                | { isActive?: boolean }
                | null
                | undefined;
              if (ecomValues?.isActive) {
                await (dispatch as unknown as (a: unknown) => unknown)(getMeasurementDetails({ isActive: true }));
              }
            })();
          }
        } else if (key === 'locationType') {
          const source = currentRelation.location?.name === 'Samples' ? sampleTypeFromStore : dynamicBomSelection;
          nextRelation = { ...currentRelation, locationType: pickFrom(source), locationPlacement: null, attribute: null, output: null, subLocation: null };
        } else if (key === 'locationPlacement') {
          nextRelation = { ...currentRelation, locationPlacement: pickFrom(setPlacementForLocation()), attribute: null, output: null, subLocation: null };
        } else if (key === 'attribute') {
          const baseAttr: SourceRelationOption[] =
            currentRelation.location?._id === 12
              ? (prouctCustmSectionAttributeList as unknown as Array<{ label: string; name: string }>).map((attributeItem) =>
                  buildOption(attributeItem.label, attributeItem.name),
                )
              : setOptionsForAttribute();
          const picked = pickFrom(baseAttr);
          nextRelation = { ...currentRelation, attribute: picked };
          const locName = currentRelation.location?.name;
          const locId = currentRelation.location?._id;
          const typeName = currentRelation.locationType?.name;
          const attrName = picked?.name;
          const outputNullConditions =
            ((['first_item', 'last_item'].includes(attrName || '') &&
              ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Samples', 'Costing_Information'].includes(
                locName || '',
              )) ||
              (['Purchase_Order'].includes(locName || '') && attrName === 'valueType')) ??
            false;
          const outputDateCondition =
            ['first_item', 'last_item'].includes(attrName || '') &&
            (['Design_Details', 'Testing', 'Care/User_Document_Information'].includes(locName || '') ||
              locId === 12 ||
              (['Bill_of_Materials'].includes(locName || '') && typeName !== 'all_item') ||
              (['Bill_of_Materials'].includes(locName || '') && typeName === 'all_item' && ['first_item', 'last_item'].includes(attrName || '')));
          if (outputNullConditions) nextRelation.output = null;
          else if (outputDateCondition) nextRelation.output = buildOption('Date Added', 'date_added');
          else if (locName === 'Purchase_Order' && attrName === 'all_item')
            nextRelation.output = buildOption('Percentage of Confirmed', 'completion_percentage');
          nextRelation.subLocation = null;
        } else if (key === 'output') {
          const useBom = currentRelation.location?.name === 'Bill_of_Materials';
          const src: SourceRelationOption[] = useBom ? setOptionsForBomOutput() : setOptionsForOutput();
          nextRelation = { ...currentRelation, output: pickFrom(src) };
        } else if (key === 'subLocation') {
          nextRelation = { ...currentRelation, subLocation: pickFrom(setSubLocationArr()) };
        }

        const locN = key === 'location' ? nextRelation.location?.name : currentRelation.location?.name;
        const placeN = key === 'locationPlacement' ? nextRelation.locationPlacement?.name : currentRelation.locationPlacement?.name;
        if (setAttrinuteToValue(locN, placeN)) nextRelation = { ...nextRelation, attribute: buildOption('Value', 'valueType') };

        const next = { ...prev, relation: nextRelation };
        onChange(next);
        return next;
      });
    },
    [
      dispatch,
      dynamicBomSelection,
      sampleTypeFromStore,
      setOptionsForAttribute,
      setPlacementForLocation,
      setOptionsForBomOutput,
      setOptionsForOutput,
      setSubLocationArr,
      onChange,
    ],
  );

  const toggleRequired = useCallback(() => {
    setLocalValue((prev) => {
      const next = { ...prev, required: !prev.required };
      onChange(next);
      return next;
    });
  }, [onChange]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, title: next };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const toggleSupplierVisible = useCallback(() => {
    setLocalValue((prev) => {
      const next = { ...prev, isVisibleToSupplier: !prev.isVisibleToSupplier };
      onChange(next);
      return next;
    });
  }, [onChange]);

  const toggleColorSwitch = useCallback(() => {
    setLocalValue((prev) => {
      const nextVal = !(prev.relation?.colorSwitch ?? prev.colorSwitch);
      const array = prev.relation?.colourCoding ??
        prev.colourArray ?? [
          { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
        ];
      const nextRelation: SourceRelationValue = { ...(prev.relation || {}), colorSwitch: nextVal, colourCoding: array };
      const next = { ...prev, colorSwitch: nextVal, relation: nextRelation };
      onChange(next);
      return next;
    });
  }, [onChange]);

  const updateColourValue = useCallback(
    (index: number, colourValue: string) => {
      setLocalValue((prev) => {
        const array = [...(prev.relation?.colourCoding ?? prev.colourArray ?? [])];
        if (array[index]) array[index] = { ...array[index], colourValue };
        const nextRelation: SourceRelationValue = { ...(prev.relation || {}), colourCoding: array };
        const next = { ...prev, colourArray: array, relation: nextRelation };
        onChange(next);
        return next;
      });
    },
    [onChange],
  );

  const changeColourInput = useCallback(
    (index: number, field: 'startPercentage' | 'endPercentage', value: string) => {
      setLocalValue((prev) => {
        const array = [...(prev.relation?.colourCoding ?? prev.colourArray ?? [])];
        if (array[index]) array[index] = { ...array[index], [field]: value };
        const nextRelation: SourceRelationValue = { ...(prev.relation || {}), colourCoding: array };
        const next = { ...prev, colourArray: array, relation: nextRelation };
        onChange(next);
        return next;
      });
    },
    [onChange],
  );

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as SourceConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  /**
   * Validates and saves the current source configuration
   * Performs comprehensive validation of all required fields based on location and type selections
   * Shows appropriate error messages for missing required fields
   */
  const handleSave = useCallback(() => {
    const rel = localValue.relation ?? emptyRelation;
    const locName = rel.location?.name;
    const needsType = ['Bill_of_Materials', 'Samples'].includes(locName || '');
    const missingLocation = !rel.location;
    const missingType = needsType && !rel.locationType;
    const missingPlacement = hideShowPlacement() && !rel.locationPlacement;
    const missingAttribute = hideShowAttribute() && !rel.attribute;
    const missingOutput = hideShowOutput() && !rel.output;
    const missingSubLocation = hideShowSubLocation() && !rel.subLocation;

    if (missingLocation) {
      toasterMsg(t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION_REQUIRED'), TOAST_DANGER);
      return;
    }
    if (missingType) {
      const typeLabel = locName === 'Samples' ? t('CUSTOM_LIBRARY.SOURCE_TYPE.SAMPLE_TYPE_REQUIRED') : t('CUSTOM_LIBRARY.SOURCE_TYPE.BOM_TYPE_REQUIRED');
      toasterMsg(typeLabel, TOAST_DANGER);
      return;
    }
    if (missingPlacement) {
      const label = placementLabel();
      toasterMsg(`${label} ${t('COMMON.IS_REQUIRED')}.`, TOAST_DANGER);
      return;
    }
    if (missingAttribute) {
      toasterMsg(t('CUSTOM_LIBRARY.SOURCE_TYPE.ATTRIBUTE_REQUIRED'), TOAST_DANGER);
      return;
    }
    if (missingOutput) {
      toasterMsg(t('CUSTOM_LIBRARY.SOURCE_TYPE.OUTPUT_REQUIRED'), TOAST_DANGER);
      return;
    }
    if (missingSubLocation) {
      toasterMsg(t('CUSTOM_LIBRARY.SOURCE_TYPE.SUB_LOCATION_REQUIRED'), TOAST_DANGER);
      return;
    }

    onChange(localValue);
    lastEmittedRef.current = localValue;
  }, [localValue, onChange, hideShowPlacement, hideShowAttribute, hideShowOutput, hideShowSubLocation, placementLabel, t]);

  const relation = localValue.relation ?? emptyRelation;

  const normalizedValue = useMemo(() => {
    const nextRelation: SourceRelationValue = {
      ...(value.relation ?? {}),
      colorSwitch: value.relation?.colorSwitch ?? value.colorSwitch ?? false,
      colourCoding: value.relation?.colourCoding ??
        value.colourArray ?? [
          { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
        ],
    };
    return {
      title: value.title ?? '',
      required: !!value.required,
      isVisibleToSupplier: value.isVisibleToSupplier ?? true,
      relation: nextRelation,
      colorSwitch: value.colorSwitch ?? value.relation?.colorSwitch ?? false,
      colourArray: value.colourArray ?? [
        { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
        { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
        { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
      ],
    };
  }, [value]);

  const hasChanges = useMemo(() => {
    return !isDeepEqual(localValue, normalizedValue);
  }, [localValue, normalizedValue]);

  const showDateDetails = useCallback(() => relation?.output?.name === 'date_added', [relation?.output?.name]);
  const disableColourSwitch = useCallback(() => {
    const locName = relation?.location?.name;
    const outputName = relation?.output?.name;
    const attributeName = relation?.attribute?.name;
    const coreBlocked = [
      'Product_Core_Information',
      'Product_Information',
      'Supplier_Information',
      'Specifications',
      'Samples',
      'Costing_Information',
      'Completion_Indicator',
    ];
    if (coreBlocked.includes(locName || '')) return true;
    if (locName === 'Bill_of_Materials' && outputName === 'valueType') return true;
    if (locName === 'Purchase_Order' && attributeName === 'valueType') return true;
    if (
      ![
        'Product_Core_Information',
        'Product_Information',
        'Supplier_Information',
        'Design_Details',
        'Bill_of_Materials',
        'Specifications',
        'Testing',
        'Care/User_Document_Information',
        'Samples',
        'Costing_Information',
        'Purchase_Order',
        'Completion_Indicator',
      ].includes(locName || '') &&
      outputName === 'valueType'
    ) {
      return true;
    }
    return false;
  }, [relation?.location?.name, relation?.output?.name, relation?.attribute?.name]);

  const colourDisallowed = disableColourSwitch();
  useEffect(() => {
    if (colourDisallowed && (localValue.relation?.colorSwitch ?? localValue.colorSwitch)) {
      setLocalValue((prev) => {
        const cleared: Array<{ colourValue: string; colourType: 'green' | 'amber' | 'red'; startPercentage: string; endPercentage: string }> = [
          { colourValue: '', colourType: 'green', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'amber', startPercentage: '', endPercentage: '' },
          { colourValue: '', colourType: 'red', startPercentage: '', endPercentage: '' },
        ];
        const nextRelation: SourceRelationValue = { ...(prev.relation || {}), colorSwitch: false, colourCoding: cleared };
        return { ...prev, colorSwitch: false, colourArray: cleared, relation: nextRelation };
      });
    }
  }, [colourDisallowed]);

  const dateColourOptions = useMemo<ReadonlyArray<OptionType>>(() => {
    const locId = localValue.relation?.location?._id as number | string | undefined;
    let subLocationDef: SubLocationDef | undefined = (cmControlSublocations as unknown as SubLocationDef[]).find((v) => v?.locationId === locId);
    if (!subLocationDef?.subLocations) {
      const findLocation = (cmControlBaseLocations as unknown as HelperLocation[]).find((l) => l?.name === localValue.relation?.location?.name);
      subLocationDef = (cmControlSublocations as unknown as SubLocationDef[]).find((v) => v?.locationId === (findLocation?._id as number | string));
    }
    const dateSubs = (subLocationDef?.subLocations || [])
      .filter((subLocationItem) => subLocationItem?.type === 'date')
      .map((subLocationItem) => ({ label: subLocationItem.label, value: subLocationItem.name } as OptionType));
    const sampleDates = (sampleTrackingDates || []).map((dateItem) => ({ label: dateItem?.name, value: dateItem?._id } as OptionType));
    return [...dateSubs, ...sampleDates];
  }, [localValue.relation?.location, sampleTrackingDates]);

  const nameBlock = (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between">
        <p className="m-0">Name</p>
      </div>
      <NewInput
        type="text"
        placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER')}
        value={localValue.title ?? ''}
        onChange={(e) => updateTitle(e.target.value)}
        disabled={disabled}
      />
    </div>
  );

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && nameBlock}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="source"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      <div className="mb-2 mt-2 d-flex align-items-center justify-content-between">
        <div className="cm-checkbox d-inline-flex align-items-center mr-4">
          <label className="cursor-pointer m-0 mt-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
          {!localValue.title?.trim() ? (
            <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
              <NewCheckbox className="scale12 ml-2" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
            </CustomTooltip>
          ) : (
            <NewCheckbox className="scale12 ml-2" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          )}
          <span className="red">*</span>
        </div>
        <div className="cm-checkbox d-inline-flex align-items-center">
          <NewCheckbox className="scale12 ml-2" checked={!!localValue.isVisibleToSupplier} onChange={toggleSupplierVisible} aria-label={t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.VISIBLE_SUPLIER')} />
          <label className="cursor-pointer m-0">{t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.VISIBLE_SUPLIER')}</label>
        </div>
      </div>
      <div className="card">
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="m-0 font-weight-500">{t('CUSTOM_MODULE.SYSTEM_CANVAS.SOURCE.SOURCED')}</span>
            {hasChanges && (
              <button
                type="button"
                onClick={handleSave}
                className="bombiix-btn btn-blue d-inline-flex align-items-center"
                aria-label={t('ACTIONS.SAVE')}
              >
                <SaveIcon size={24} className="text-white align-text-bottom" />
              </button>
            )}
          </div>
          {!showWidthRow && nameBlock}
          <div className="pt-2">
            <div className="mt-2">
              <p className="asterisk-req f-14 mb-1">
                {t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION')} <span>*</span>
              </p>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                name="location"
                options={toOptions(locationOptions)}
                value={toSelectValue(relation.location)}
                onChange={(value) => updateRelation('location', selectedValue(value))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                isDisabled={disabled}
              />
            </div>

            {['Bill_of_Materials', 'Samples'].includes(relation.location?.name || '') && (
              <div className="mt-2">
                <p className="asterisk-req f-14 mb-1">
                  {t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_TYPE')} <span>*</span>
                </p>
                <NewSelectMultiSelect
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="locationType"
                  options={toOptions(relation.location?.name === 'Samples' ? (sampleTypeFromStore.length ? sampleTypeFromStore : []) : dynamicBomSelection)}
                  value={toSelectValue(relation.locationType)}
                  onChange={(value) => updateRelation('locationType', selectedValue(value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                  isDisabled={disabled}
                />
              </div>
            )}

            {hideShowPlacement() && (
              <div className="mt-2">
                <p className="asterisk-req f-14 mb-1">
                  {t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_PLACEMENT')} <span>*</span>
                </p>
                <NewSelectMultiSelect
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="locationPlacement"
                  options={toOptions(setPlacementForLocation())}
                  value={toSelectValue(relation.locationPlacement)}
                  onChange={(value) => updateRelation('locationPlacement', selectedValue(value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_PLACEMENT')}
                  isDisabled={disabled}
                />
              </div>
            )}

            {hideShowAttribute() && (
              <div className="mt-2">
                <p className="asterisk-req f-14 mb-1">
                  {t('CUSTOM_LIBRARY.SOURCE_TYPE.ATTRIBUTE')} <span>*</span>
                </p>
                <NewSelectMultiSelect
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="attribute"
                  options={toOptions(
                    (relation.location?._id === 12
                      ? (prouctCustmSectionAttributeList as Array<{ label: string; name: string }>).map((attributeItem) =>
                          buildOption(attributeItem.label, attributeItem.name),
                        )
                      : setOptionsForAttribute()) || [],
                  )}
                  value={toSelectValue(relation.attribute)}
                  onChange={(value) => updateRelation('attribute', selectedValue(value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                  isDisabled={disabled}
                />
              </div>
            )}

            {hideShowOutput() && (
              <div className="mt-2">
                <p className="asterisk-req f-14 mb-1">
                  {t('CUSTOM_LIBRARY.SOURCE_TYPE.OUTPUT')} <span>*</span>
                </p>
                <NewSelectMultiSelect
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="output"
                  options={toOptions(relation.location?.name === 'Bill_of_Materials' ? setOptionsForBomOutput() : setOptionsForOutput())}
                  value={toSelectValue(relation.output)}
                  onChange={(value) => updateRelation('output', selectedValue(value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                  isDisabled={disabled}
                />
              </div>
            )}

            {hideShowSubLocation() && (
              <div className="mt-2">
                <p className="asterisk-req f-14 mb-1">
                  {t('CUSTOM_LIBRARY.SOURCE_TYPE.SUB_LOCATION')} <span>*</span>
                </p>
                <NewSelectMultiSelect
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="subLocation"
                  options={toOptions(setSubLocationArr())}
                  value={toSelectValue(relation.subLocation)}
                  onChange={(value) => updateRelation('subLocation', selectedValue(value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                  isDisabled={disabled}
                />
              </div>
            )}
          </div>

          {!colourDisallowed && (
            <div className="cm-colour-coding">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-inline-flex align-items-center design-switch">
                  <NewCheckbox className="form-check-input comment-switch-input" id="scColourSwitch" name="colourSwitch" onChange={toggleColorSwitch} checked={!!(localValue.relation?.colorSwitch ?? localValue.colorSwitch)} disabled={disabled} />
                  <label className="switch-text font-heavy align-items-center mt-03" htmlFor="scColourSwitch">
                    {t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.COLOUR_CODING')}
                  </label>
                </div>
              </div>
              {(localValue.relation?.colorSwitch ?? localValue.colorSwitch) && (
                <div className="cm-colour-coding__rules">
                  {(
                    ((localValue.relation?.colourCoding ?? localValue.colourArray) || []) as Array<{
                      colourValue: string;
                      colourType: 'green' | 'amber' | 'red';
                      startPercentage: string;
                      endPercentage: string;
                    }>
                  ).map((rule, index) => {
                    const isBetween = rule.colourValue === 'between';
                    const isDate = showDateDetails();
                    const unit = isDate ? 'days' : '%';
                    return (
                      <div className="cm-colour-rule" key={`cc-${index}`}>
                        <div className="cm-colour-rule__label">
                          <span className={`cm-colour-rule__swatch cm-colour-rule__swatch--${rule?.colourType}`} aria-hidden="true" />
                          <span>{index === 0 ? 'if before the time frame of' : index === 1 ? 'if within time frame' : 'if exceeds time frame of'}</span>
                        </div>
                        <div className="cm-colour-rule__inputs">
                          <div className="cm-colour-rule__select">
                            {isDate ? (
                              <NewSelectMultiSelect
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                name={`colourDateRef_${index}`}
                                options={dateColourOptions.filter(
                                  (option) =>
                                    !(localValue.relation?.colourCoding ?? [])
                                      .map((colourCodingItem) => colourCodingItem.colourValue)
                                      .includes(option.value as string),
                                )}
                                value={dateColourOptions.find((option) => option.value === rule?.colourValue) || null}
                                onChange={(value) => updateColourValue(index, (selectedValue(value) as string) || '')}
                                placeholder={t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.SELECT_COLUMN')}
                                isDisabled={disabled}
                              />
                            ) : (
                              <NewSelectMultiSelect
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                name={`colourValue_${index}`}
                                options={columnValueOptions.filter(
                                  (option) =>
                                    !(localValue.relation?.colourCoding ?? [])
                                      .map((colourCodingItem) => colourCodingItem.colourValue)
                                      .includes(option.value as string),
                                )}
                                value={rule?.colourValue ? columnValueOptions.find((option) => option.value === rule?.colourValue) ?? null : null}
                                onChange={(value) => updateColourValue(index, (selectedValue(value) as string) || '')}
                                placeholder={t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.SELECT_VALUE')}
                                isDisabled={disabled}
                              />
                            )}
                          </div>
                          {(isBetween || !isDate) && (
                            <NewInput
                              className="text-bordered percentaage-input cm-colour-rule__number"
                              onChange={(e) => changeColourInput(index, 'startPercentage', e?.target?.value)}
                              disabled={!rule?.colourValue}
                              value={rule?.startPercentage}
                              type="number"
                              name="colourStateValue"
                              placeholder={`0 ${unit}`}
                            />
                          )}
                          {!isBetween && !isDate && <span>{unit}</span>}
                          {isBetween && (
                            <>
                              <span>{isDate ? '' : 'and'}</span>
                              <NewInput
                                className="text-bordered percentaage-input cm-colour-rule__number"
                                onChange={(e) => changeColourInput(index, 'endPercentage', e?.target?.value)}
                                disabled={!rule?.colourValue}
                                value={rule?.endPercentage}
                                name="colourStateValue"
                                type="number"
                                placeholder={`0 ${unit}`}
                              />
                              <span>{unit}</span>
                            </>
                          )}
                          {isDate && !isBetween && <span>{unit}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(SourceControl);
